import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, RotateCcw, Briefcase, Sparkles } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  tumPozisyonlariGetir,
  pozisyonOlustur,
  pozisyonSil,
  pozisyonGeriYukle,
} from '@/lib/services/pozisyon-service'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PozisyonFormModal } from './PozisyonFormModal'
import type { Pozisyon } from '@/types'

const ONERILER: string[] = [
  'Kasiyer',
  'Rafçı',
  'Depocu',
  'Kasap',
  'Manav',
  'Şarküteri',
  'Fırıncı',
  'Güvenlik',
  'Temizlikçi',
  'Şoför',
  'Stajyer',
]

export function PozisyonlarPage() {
  const { firebaseUser } = useAuth()
  const [pozisyonlar, setPozisyonlar] = useState<Pozisyon[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState<string | null>(null)
  const [pasifGoster, setPasifGoster] = useState(false)

  const [formAcik, setFormAcik] = useState(false)
  const [duzenlenecek, setDuzenlenecek] = useState<Pozisyon | null>(null)
  const [silinecek, setSilinecek] = useState<Pozisyon | null>(null)
  const [oneriEkleniyor, setOneriEkleniyor] = useState(false)

  async function yukle() {
    try {
      setYukleniyor(true)
      const data = await tumPozisyonlariGetir()
      setPozisyonlar(data)
      setHata(null)
    } catch (err) {
      console.error('Pozisyonlar yüklenemedi:', err)
      setHata('Pozisyonlar yüklenemedi. Lütfen sayfayı yenileyin.')
    } finally {
      setYukleniyor(false)
    }
  }

  useEffect(() => {
    yukle()
  }, [])

  async function silOnay() {
    if (!silinecek) return
    try {
      await pozisyonSil(silinecek.id)
      setSilinecek(null)
      await yukle()
    } catch (err) {
      alert('Pozisyon silinemedi: ' + (err as Error).message)
    }
  }

  async function geriYukle(p: Pozisyon) {
    try {
      await pozisyonGeriYukle(p.id)
      await yukle()
    } catch (err) {
      alert('Pozisyon geri yüklenemedi: ' + (err as Error).message)
    }
  }

  async function oneriyiEkle(isim: string) {
    if (!firebaseUser) return
    if (pozisyonlar.some(p => p.isim.toLowerCase() === isim.toLowerCase())) {
      alert(`"${isim}" zaten ekli.`)
      return
    }
    setOneriEkleniyor(true)
    try {
      await pozisyonOlustur({ isim }, firebaseUser.uid)
      await yukle()
    } catch (err) {
      alert('Eklenemedi: ' + (err as Error).message)
    } finally {
      setOneriEkleniyor(false)
    }
  }

  const gosterilen = pasifGoster ? pozisyonlar : pozisyonlar.filter(p => p.aktif)
  const eklenmemisOneriler = ONERILER.filter(
    o => !pozisyonlar.some(p => p.isim.toLowerCase() === o.toLowerCase())
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pozisyonlar</h1>
          <p className="text-sm text-gray-600 mt-1">
            {gosterilen.length} pozisyon
            {!pasifGoster && pozisyonlar.length > gosterilen.length && (
              <button
                onClick={() => setPasifGoster(true)}
                className="ml-2 text-brand-600 hover:underline"
              >
                ({pozisyonlar.length - gosterilen.length} pasif gizli)
              </button>
            )}
            {pasifGoster && (
              <button
                onClick={() => setPasifGoster(false)}
                className="ml-2 text-brand-600 hover:underline"
              >
                (sadece aktifler)
              </button>
            )}
          </p>
        </div>
        <button
          onClick={() => {
            setDuzenlenecek(null)
            setFormAcik(true)
          }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Yeni Pozisyon</span>
        </button>
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 mb-4">
          {hata}
        </div>
      )}

      {!yukleniyor && eklenmemisOneriler.length > 0 && (
        <div className="card mb-4 bg-brand-50 border-brand-200">
          <div className="flex items-start gap-3 mb-3">
            <Sparkles className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">Hızlı Ekle</h3>
              <p className="text-xs text-gray-600">
                Yaygın pozisyonları tek tıkla ekleyebilirsin
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {eklenmemisOneriler.map(o => (
              <button
                key={o}
                onClick={() => oneriyiEkle(o)}
                disabled={oneriEkleniyor}
                className="px-3 py-1.5 bg-white text-brand-700 text-sm font-medium rounded-full border border-brand-300 hover:bg-brand-100 disabled:opacity-50 transition-colors"
              >
                + {o}
              </button>
            ))}
          </div>
        </div>
      )}

      {yukleniyor && (
        <div className="card text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mb-3"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      )}

      {!yukleniyor && gosterilen.length === 0 && (
        <div className="card text-center py-12">
          <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-bold text-gray-900 mb-2">Henüz pozisyon yok</h3>
          <p className="text-gray-600 mb-6">
            Yukarıdaki "Hızlı Ekle" ile başlayabilir veya kendi pozisyonunu tanımlayabilirsin.
          </p>
        </div>
      )}

      {!yukleniyor && gosterilen.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {gosterilen.map(p => (
            <PozisyonKarti
              key={p.id}
              pozisyon={p}
              onDuzenle={() => {
                setDuzenlenecek(p)
                setFormAcik(true)
              }}
              onSil={() => setSilinecek(p)}
              onGeriYukle={() => geriYukle(p)}
            />
          ))}
        </div>
      )}

      <PozisyonFormModal
        acik={formAcik}
        pozisyon={duzenlenecek}
        onKapat={() => setFormAcik(false)}
        onKaydedildi={() => {
          setFormAcik(false)
          yukle()
        }}
      />

      <ConfirmDialog
        acik={!!silinecek}
        baslik="Pozisyonu Sil"
        mesaj={`"${silinecek?.isim}" pozisyonu pasif duruma alınacak. Mevcut atamalar etkilenmez ama yeni personel atanamaz. Devam mı?`}
        onayMetni="Pasif Yap"
        tehlikeli
        onOnay={silOnay}
        onIptal={() => setSilinecek(null)}
      />
    </div>
  )
}

interface PozisyonKartiProps {
  pozisyon: Pozisyon
  onDuzenle: () => void
  onSil: () => void
  onGeriYukle: () => void
}

function PozisyonKarti({ pozisyon, onDuzenle, onSil, onGeriYukle }: PozisyonKartiProps) {
  return (
    <div className={`card flex items-center justify-between gap-3 ${!pozisyon.aktif ? 'opacity-60' : ''}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="bg-brand-50 p-2 rounded-lg flex-shrink-0">
          <Briefcase className="w-5 h-5 text-brand-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-gray-900 truncate">{pozisyon.isim}</h3>
            {!pozisyon.aktif && (
              <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">
                Pasif
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        {pozisyon.aktif ? (
          <>
            <button
              onClick={onDuzenle}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-gray-900 transition-colors"
              title="Düzenle"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={onSil}
              className="p-2 hover:bg-red-50 rounded-lg text-gray-600 hover:text-red-600 transition-colors"
              title="Sil"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        ) : (
          <button
            onClick={onGeriYukle}
            className="p-2 hover:bg-green-50 rounded-lg text-gray-600 hover:text-green-600 transition-colors"
            title="Geri Yükle"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}
